import {
    OnToggleExpand,
    OnToggleSelectNode,
    TreeBaseItem,
    TreeViewClasses,
    TreeViewProps,
} from './types';
import {List} from '@mui/material';
import BaseTreeNode from './BaseTreeNode';
import {useCallback} from 'react';
import {findNodeById, getFlattenNodes} from './treeHelper';

export default function TreeView<D extends TreeBaseItem>({
    nodes,
    renderNodeLabel,
    loadChildren,
    onToggleSelect,
    onToggleExpand,
    selectShouldCollapse,
    selectShouldExpand = true,
    collapseShouldUnselectChildren = true,
    required,
    multiple = false,
    onSelectionChange,
    selectedNodes,
    expandedNodes,
    setExpandedNodes,
    setSelectedNodes,
    ...nodeProps
}: TreeViewProps<D>) {
    const onToggleExpandInternal = useCallback<OnToggleExpand<D>>(
        async (node, expanded) => {
            if (
                expanded &&
                node.hasChildren &&
                (node.children === undefined || false === node.childrenLoaded)
            ) {
                await loadChildren?.(node);
            }

            if (
                !expanded &&
                collapseShouldUnselectChildren &&
                node.children &&
                node.children.length > 0
            ) {
                const allChildren = getFlattenNodes(node.children);
                const idsToUnselect = allChildren.map(node => node.id);
                const updateSelectedNodes = (prev: typeof selectedNodes) =>
                    prev.filter(id => !idsToUnselect.includes(id));

                setSelectedNodes(updateSelectedNodes);
                onSelectionChange?.(
                    updateSelectedNodes(selectedNodes).map(id => {
                        const n = findNodeById(nodes, id);
                        if (node.id === id) {
                            return {
                                ...n,
                                ...(node.id === id ? node : {}),
                            };
                        }

                        return n;
                    })
                );

                if (onToggleSelect) {
                    allChildren.forEach(n => onToggleSelect(n, false));
                }
            }

            await onToggleExpand?.(node, expanded);

            setExpandedNodes(prev => {
                if (expanded) {
                    return [...prev, node.id];
                } else {
                    return prev.filter(id => id !== node.id);
                }
            });
        },
        [
            setExpandedNodes,
            onSelectionChange,
            selectedNodes,
            setSelectedNodes,
            onToggleExpand,
            loadChildren,
        ]
    );

    const onToggleSelectInternal = useCallback<OnToggleSelectNode<D>>(
        (node, selected) => {
            if (
                selected &&
                selectShouldExpand &&
                !expandedNodes.includes(node.id)
            ) {
                onToggleExpandInternal(node, true);
            } else if (
                !selected &&
                selectShouldCollapse &&
                expandedNodes.includes(node.id)
            ) {
                onToggleExpandInternal(node, false);
            }

            onToggleSelect?.(node, selected);

            const updateSelectedNodes = (prev: typeof selectedNodes) => {
                if (multiple) {
                    if (selected) {
                        return [...prev, node.id];
                    } else {
                        return prev.filter(id => id !== node.id);
                    }
                } else {
                    if (required && !selected) {
                        return prev;
                    }

                    return selected ? [node.id] : [];
                }
            };

            setSelectedNodes(updateSelectedNodes);

            onSelectionChange?.(
                updateSelectedNodes(selectedNodes).map(id => {
                    const n = findNodeById(nodes, id);
                    if (node.id === id) {
                        return {
                            ...n,
                            ...(node.id === id ? node : {}),
                        };
                    }

                    return n;
                })
            );
        },
        [
            selectedNodes,
            setSelectedNodes,
            onToggleSelect,
            onSelectionChange,
            onToggleExpandInternal,
            nodes,
        ]
    );

    return (
        <List
            dense={true}
            sx={{
                [`.${TreeViewClasses.Node}`]: {
                    'display': 'flex',
                    'flexDirection': 'row',
                    'gap': 1,
                    'alignItems': 'center',
                    'borderRadius': 1,
                    '& .MuiSvgIcon-root': {
                        fontSize: 20,
                    },
                },
                [`.${TreeViewClasses.NodeArrow}`]: {
                    width: 24,
                    display: 'flex',
                    alignItems: 'center',
                    ml: -1,
                    mr: -0.5,
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease-in-out',
                    transform: 'rotate(-90deg)',
                },
                [`.${TreeViewClasses.NodeExpanded}`]: {
                    [`.${TreeViewClasses.NodeArrow}`]: {
                        transform: 'rotate(0deg)',
                    },
                },
                [`.${TreeViewClasses.NodeDisabled}`]: {
                    '.MuiListItemText-root': {
                        opacity: 0.5,
                        cursor: 'not-allowed',
                    },
                },
                [`.${TreeViewClasses.NodeChildren}`]: {
                    ml: 2.5,
                    borderLeft: '1px dashed rgba(0, 0, 0, 0.12)',
                },
                [`.${TreeViewClasses.NodeLabel}`]: {
                    flexGrow: 1,
                },
            }}
        >
            {nodes.map(node => (
                <BaseTreeNode<D>
                    {...nodeProps}
                    onToggleSelect={onToggleSelectInternal}
                    onToggleExpand={onToggleExpandInternal}
                    level={0}
                    key={node.id}
                    expandedNodes={expandedNodes}
                    selectedNodes={selectedNodes}
                    node={node}
                    renderNodeLabel={renderNodeLabel}
                />
            ))}
        </List>
    );
}
